<template>
  <portal to="modal">
    <div
      ref="background"
      class="background"
      v-if="!isInPrintMode"
      :aria-hidden="suppressed ? 'true' : undefined"
      :class="{
        'center-vertically': centerVertically,
        'cover-snackbars': coverSnackbars,
        'fade-away': fadeAway,
        'fade-in': fadeIn,
        suppressed: suppressed
      }"
      @click.self="$emit('outsideClick')"
    >
      <slot></slot>
    </div>
  </portal>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import { mapState } from 'pinia'
import { useAppStore } from '../../stores/AppStore'
import { lockPageScroll, unlockPageScroll } from '@/util/page-scroll-lock'

// A visual viewport this much shorter than the layout viewport means the on-screen keyboard is up,
// rather than a rounding difference or a URL bar (which `100dvh` already tracks on its own).
const KEYBOARD_HEIGHT_THRESHOLD_PX = 120

@Component({
  computed: {
    ...mapState(useAppStore, ['isInPrintMode'])
  }
})
export default class NBackdrop extends Vue {
  @Prop({ default: false, type: Boolean }) centerVertically!: boolean
  @Prop({ default: false, type: Boolean }) coverSnackbars!: boolean
  @Prop({ default: false, type: Boolean }) fadeAway!: boolean
  @Prop({ default: false, type: Boolean }) suppressed!: boolean
  private isInPrintMode!: boolean
  scrollListener!: (ev: Event) => void
  private enableScrollTimeout: number | undefined = undefined
  private holdsScrollLock = false

  fadeIn = false

  @Ref('background') backgroundRef!: HTMLElement

  @Watch('isInPrintMode')
  onIsInPrintModeChange() {
    // PDF generation can flip this while a modal is open, and the backdrop is v-if'd out with it.
    if (this.isInPrintMode) {
      this.releaseScrollLock()
    } else {
      this.acquireScrollLock()
    }
  }

  @Watch('fadeAway')
  onFadeAwayChange() {
    if (this.fadeAway && this.backgroundRef) {
      this.backgroundRef.style.overflowY = 'hidden'
    }
  }

  private acquireScrollLock() {
    if (this.holdsScrollLock) {
      return
    }
    this.holdsScrollLock = true
    lockPageScroll()
  }

  private releaseScrollLock() {
    if (!this.holdsScrollLock) {
      return
    }
    this.holdsScrollLock = false
    unlockPageScroll()
  }

  mounted() {
    // The page cannot be scrolled while the backdrop is up, so hide its scrollbar too.
    if (!this.isInPrintMode) {
      this.acquireScrollLock()
    }

    const appElement = document.getElementById('app')
    if (appElement) {
      const scrollY = window.scrollY
      this.scrollListener = (event: Event) => {
        event.stopPropagation()
        event.preventDefault()
        window.scrollTo({ top: scrollY })
      }
      window.addEventListener('scroll', this.scrollListener, { passive: false })
    }
    this.$nextTick(() => {
      this.fadeIn = true
    })
    // The backdrop can be gone before this lands - the onboarding hand-off closes one modal and
    // opens the next in the same gesture - and the ref is also empty whenever the portal target
    // has not taken the content yet, so it is not guaranteed to be there in 500ms.
    this.enableScrollTimeout = window.setTimeout(() => {
      if (this.backgroundRef) {
        this.backgroundRef.style.overflowY = 'auto'
      }
    }, 500)

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.resizeHandler)
    }
  }

  /**
   * URL-bar show/hide is handled by the stylesheet's `100dvh` now, so this only has to cover the one
   * thing dvh cannot see: the on-screen keyboard, which shrinks the visual viewport without changing
   * the layout viewport. Anything else clears the inline override and hands sizing back to the CSS.
   *
   * The old comparison was against `window.outerHeight` (the whole browser window, chrome included),
   * which is unrelated to the page's viewport and is unreliable under device emulation.
   */
  resizeHandler() {
    if (!this.backgroundRef) {
      return
    }
    const visualViewport = window.visualViewport!
    const keyboardIsOpen = window.innerHeight - visualViewport.height > KEYBOARD_HEIGHT_THRESHOLD_PX

    if (keyboardIsOpen) {
      this.backgroundRef.style.height = `${visualViewport.height}px`
      return
    }

    this.backgroundRef.style.removeProperty('height')
  }

  beforeDestroy() {
    this.releaseScrollLock()
    window.clearTimeout(this.enableScrollTimeout)
    const appElement = document.getElementById('app')
    if (appElement) {
      window.removeEventListener('scroll', this.scrollListener)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.resizeHandler)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.background {
  overflow-x: hidden;
  overflow-y: hidden;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;

  // One unit, the same one NModalBody uses. This used to be `min-height: 100svh; height: 100%`:
  // svh is the viewport with the browser UI *expanded* while the modal inside measured vh (UI
  // retracted), so backdrop and modal disagreed about where the bottom of the screen was.
  height: 100dvh;
  z-index: $notch-layer-overlay;
  background-color: rgba($notch-color-neutral-grey-400, 0.8);
  opacity: 0;

  &.center-vertically {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &.cover-snackbars {
    z-index: $notch-layer-covering-overlay;
  }

  &.fade-in {
    opacity: 1;
    transition: opacity 200ms ease-in;
  }

  &.fade-away {
    opacity: 0;
    transition: opacity 300ms ease-out;
  }

  &.suppressed {
    visibility: hidden;
    pointer-events: none;
  }
}
</style>
